import { FC, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import moment from "moment";
import { ListProduct, Pipeline } from "../../../interfaces/Pipeline";
import { useSettingsStore } from "../../../stores/SettingsStore";
import useUser from "../../../hooks/User";
import { CustomField } from "../../../interfaces/Settings";
import { useCustomeStore } from "../../../stores/CustomeStore";
import { usePipelineStore } from "../../../stores/PipelineStore";
import { currencyFormatter, customNumberFormat, customNumberFormatString, getDateRangeDuration, getDateSummary, isColorDarkFromHex } from "../../../functions/general";
import { ExpandableElement } from "../../General/ExpandableElement";
import axios from "axios";
import useAccessToken from "../../../hooks/AccessToken";
import useSWR from "swr";
import BadgeTooltip from "../../General/BadgeTooltip";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { Label } from "../../../interfaces/Label";
import { Log, LogAttachment } from "../../../interfaces/Log";
import { calculateDayDurations } from "../../../functions/calculateDuration";
import { returnCustomFieldMeeting, returnCustomFieldMerchant, returnCustomFieldPipeline, returnFile, returnImage, returnInvoiceReminder, returnLabel, returnLink, returnMerchantNamePipeline, returnMultiple, returnOption, returnParagraph, returnProduct, returnProgress, returnQtyProduct, returnSource } from "../../General/DetailMasterElement";
import useModalEffect from "../../../hooks/useModalEffect";
import { DetailProductPipeline } from "../../Pipelines/DetailProductPipeline";
import { checkIsCompanyTanrise, checkIsNewProgress } from "../../../functions/customCase";

interface DetailPipelineProps {
    dataPipeline: Pipeline
    setShowDetailPipeline: React.Dispatch<React.SetStateAction<boolean>>
}

const ChatDetailPipeline: FC<DetailPipelineProps> = ({ dataPipeline, setShowDetailPipeline }) => {
    const API_URL = process.env.REACT_APP_API_URL
    const token = useAccessToken();
    const intl = useIntl()
    const [customLead, setCustomLead] = useState({} as any)
    const [customFields, setCustomFields] = useState<CustomField>({})
    const { settings } = useSettingsStore()
    const user = useUser()
    const { companyId, keyCustomfield } = useCustomeStore()
    const [data, setData] = useState<Pipeline>()
    const [log, setLog] = useState<Log[]>([])
    const { selectMultiPipeline, simplePipelines } = usePipelineStore()
    const [displayMode, setDisplayMode] = useState<string>('pipeline');
    const [showDetailProductModal, setShowDetailProductModal] = useState<boolean>(false);

    const fetcher = (url: string) => axios.get(url, {
        headers: {
            'X-Auth-token': token
        }
    }).then(res => res.data.data).catch(error => {
        throw error.response ? error.response.data : error;
    })

    const { data: dataDetailPipeline = {}, error: dataDetailPipelineError, isLoading: isLoadingDataDetailPipeline } = useSWR(dataPipeline && displayMode === 'pipeline' ? `${API_URL}v2/lead/${dataPipeline.id}` : null, fetcher)
    const { data: dataLogPipeline = [], error: dataLogPipelineError, isLoading: isLoadingDataLogPipeline } = useSWR(dataPipeline && displayMode === 'log' ? `${API_URL}lead_logs/?lead_id=${dataPipeline.id}` : null, fetcher)

    useEffect(() => {
        if (!isLoadingDataDetailPipeline && Object.keys(dataDetailPipeline).length > 0) {
            setData(dataDetailPipeline)
        }
    }, [dataDetailPipeline])

    useEffect(() => {
        if (!isLoadingDataLogPipeline && dataLogPipeline.length > 0) {
            var data: Log[] = dataLogPipeline

            if (user.data.company_id === companyId.juraganMaterial) {
                data.forEach((eachData: any, index: number) => {
                    if (index !== 0) {
                        eachData.duration = getDateRangeDuration(data[index].date_created, data[index - 1].date_created);
                    } else {
                        eachData.duration = "-"
                    }
                })
            } else {
                data.forEach((eachData: any, index: number) => {
                    if (index < data.length - 1) {
                        if (user.data.company_id === companyId.esb) {
                            eachData.duration = calculateDayDurations(data[index + 1].date_created, data[index].date_created);
                        } else {
                            eachData.duration = getDateRangeDuration(data[index + 1].date_created, data[index].date_created);
                        }
                    } else {
                        eachData.duration = "-"
                    }
                })
            }

            setLog(data)
        }
    }, [dataLogPipeline])

    useEffect(() => {
        if (data) {
            const customData: any = {
                templateName: {
                    name: 'Database',
                    value: dataPipeline.lead_template_name
                },
                date_created: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.TYPE.DATE_CREATED' }),
                    value: moment(data.date_created).format("DD MMM YYYY HH:mm")
                },
                merchant: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NAME_TITLE' }, { title: settings.merchant_title }),
                    value: returnMerchantNamePipeline(data)
                },
                pic: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PIC' }),
                    value: data.owner_name
                },
                total_log: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.TOTAL_LOGS' }),
                    value: data.lead_logs_count && customNumberFormat(data.lead_logs_count)
                },
                progress: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PROGRESS' }),
                    value: returnProgress(data, intl, companyId, user)
                },
                phone: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PHONE' }),
                    value: data.merchant_phone
                },
                age: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.AGE' }),
                    value: getDateSummary(data.lead_age)
                },
                source: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.SOURCE' }),
                    value: returnSource(data)
                },
                product: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PRODUCT' }),
                    value: returnProduct(data, setShowDetailProductModal)
                },
                ...(selectMultiPipeline.isFeatureLabelEnabled && {
                    label: {
                        name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.LABEL' }),
                        value: returnLabel(data)
                    },
                }),
                ...(user.data.company_name === "Maspion Square"
                    ? {
                        nominal_without_vat: {
                            name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NOMINAL_BEFORE_VAT' }) + ` (${selectMultiPipeline.currencyCode})`,
                            value: `${data.nominal_without_vat && currencyFormatter(data.nominal_without_vat, selectMultiPipeline.currencyIsRounded, selectMultiPipeline.currencyCode, { showPrefix: true })}`,
                        },
                    }
                    : {}),
                nominal: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NOMINAL' }) + ` (${selectMultiPipeline.currencyCode})`,
                    value: `${data.nominal && currencyFormatter(data.nominal, selectMultiPipeline.currencyIsRounded, selectMultiPipeline.currencyCode, { showPrefix: true })}`,
                },
                product_qty: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.SUM_PRODUCTS' }),
                    value: returnQtyProduct(data)
                },
                creator: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.CREATED_BY' }),
                    value: data.creator_name
                },
                start_date: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.DATE_STARTED' }),
                    value: moment(data.date_start).format("DD MMM YYYY HH:mm")
                },
                end_date: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.DATE_ENDED' }),
                    value: data.date_end ? moment(data.date_end).format("DD MMM YYYY HH:mm") : "-"
                },
                last_update_date: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.DATE_LAST_UPDATED' }),
                    value: data.date_last_update ? moment(data.date_last_update).format("DD MMM YYYY HH:mm") : "-"
                },
                note: {
                    name: (user.data.company_id === companyId.elgisa || user.data.company_id === companyId.looyalIT) ? "Opportunity" : intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NOTES' }),
                    value: data.note ? <ExpandableElement text={(data.note).replaceAll('\n', '<br/>')} maxHeight={100} /> : "-"
                },
            }

            var newData = {
                ...customData,
                ...Object.keys(customFields).reduce((acc, key) => {
                    let value;
                    const dataCustomFields = data.custom_fields[key]
                    const dataInfoCustomFields = (data as any)?.info_custom_fields?.[key];

                    if (customFields[key].type === 'invoice_reminder' && dataCustomFields) {
                        if (JSON.parse(dataCustomFields as string).invoice_url && JSON.parse(dataCustomFields as string).date_due) {
                            value = returnInvoiceReminder(JSON.parse(dataCustomFields as string).invoice_url, JSON.parse(dataCustomFields as string).date_due, intl)
                        } else if (JSON.parse(dataCustomFields as string).invoice_url && !JSON.parse(dataCustomFields as string).date_due) {
                            value = <a href={JSON.parse(dataCustomFields as string).invoice_url} target="_blank" rel="noopener noreferrer">{intl.formatMessage({ id: "FORM.ACTION.VIEW_INVOICE" })}</a>
                        } else if (!JSON.parse(dataCustomFields as string).invoice_url && JSON.parse(dataCustomFields as string).date_due) {
                            value = moment(JSON.parse(dataCustomFields as string).date_due).format("DD MMM YYYY")
                        } else value = "-"
                    }
                    else if (customFields[key].type === 'date') {
                        value = dataCustomFields ? moment(dataCustomFields).format("DD MMM YYYY") : "-";
                    } else if (customFields[key].type === 'image' || customFields[key].type === 'images') {
                        value = dataCustomFields ? returnImage(dataCustomFields, intl) : "-";
                    } else if (customFields[key].type === 'file' || customFields[key].type === 'files') {
                        value = dataCustomFields ? returnFile(dataCustomFields, intl) : "-";
                    } else if (customFields[key].type === 'multiple' || customFields[key].type === 'multipleOption') {
                        value = dataCustomFields ? returnMultiple(dataCustomFields as string) : "-";
                    } else if (customFields[key].type === 'link') {
                        value = dataCustomFields ? returnLink(dataCustomFields as string) : "-";
                    } else if (customFields[key].type === 'serial') {
                        value = typeof dataCustomFields === 'string' ? dataCustomFields : (dataCustomFields as string[])?.length > 0 ? (dataCustomFields as string[]).join('') : '-';
                    } else if (customFields[key].type === 'merchant') {
                        value = dataInfoCustomFields && dataInfoCustomFields.length > 0 ? dataInfoCustomFields.map((item: any, index: number) => (
                            <>
                                {index > 0 && ', '}
                                {returnCustomFieldMerchant(item)}
                            </>
                        )) : "-";
                    } else if (customFields[key].type === 'user') {
                        value = dataInfoCustomFields ? dataInfoCustomFields.map((item: any) => item.name).join(', ') : "-";
                    } else if (customFields[key].type === 'dateTimeRange') {
                        value = dataCustomFields ? (dataCustomFields as string[]).map((item: any) => moment(item).format("DD MMM YYYY HH:mm")).join(' - ') : '-';
                    } else if (customFields[key].type === 'pipeline') {
                        let result: any = simplePipelines.find((item: any) => item.id === (dataInfoCustomFields?.[0] as any)?.id)

                        if (result) {
                            let name = result.name + " / " + result.nominal + " / " + moment(result.dateCreated).format('DD MMM YYYY')
                            let idTemplate = (simplePipelines as any).find((item: any) => item.id === result.id)?.leadTemplateId
                            let idPipeline = result.id
                            value = returnCustomFieldPipeline(idTemplate!, idPipeline, name);
                        } else value = "-"
                    } else if (customFields[key].type === 'meeting') {
                        value = dataInfoCustomFields && dataInfoCustomFields.length > 0 ? dataInfoCustomFields.map((item: any, index: number) => (
                            <>
                                {index > 0 && ', '}
                                {returnCustomFieldMeeting(item)}
                            </>
                        )) : "-";
                    } else if (customFields[key].type === 'time') {
                        value = dataCustomFields ? moment(dataCustomFields).format('HH:mm') : '-'
                    } else if (customFields[key].type === 'dateTime') {
                        value = dataCustomFields ? moment(dataCustomFields).format('DD MMM YYYY HH:mm') : '-'
                    } else if (customFields[key].type === 'number') {
                        value = dataCustomFields ? customNumberFormatString((dataCustomFields.toString())) : "-";
                    } else if (customFields[key].type === 'option') {
                        value = dataCustomFields ? returnOption(dataCustomFields as string) : "-";
                    } else if (customFields[key].type === 'paragraph') {
                        value = dataCustomFields ? returnParagraph(dataCustomFields as string) : "-";
                    } else if (customFields[key].type === 'product') {
                        value = dataInfoCustomFields ? dataInfoCustomFields.map((item: any) => item.product_name).join(', ') : "-";
                    } else {
                        value = dataCustomFields ? dataCustomFields : "-";
                    }

                    return {
                        ...acc,
                        [key]: {
                            name: customFields[key].name,
                            value
                        }
                    };
                }, {})
            };

            if (user.data.company_id === companyId.enterpriseSales) {
                newData = {
                    ...newData,
                    [keyCustomfield.serial.enterpriseSales]: {
                        name: 'Invoice',
                        value: data.custom_fields[keyCustomfield.serial.enterpriseSales] ? (data.custom_fields[keyCustomfield.serial.enterpriseSales] as string[]).join('') : "-"
                    }
                }
            }

            if (user.data.company_id === companyId.enterpriseService) {
                newData = {
                    ...newData,
                    [keyCustomfield.serial.enterpriseService]: {
                        name: 'Invoice',
                        value: data.custom_fields[keyCustomfield.serial.enterpriseService] ? (data.custom_fields[keyCustomfield.serial.enterpriseService] as string[]).join('') : "-"
                    }
                }
            }

            setCustomLead(newData);
        }
    }, [customFields, data])

    useEffect(() => {
        if (Object.keys(selectMultiPipeline).length !== 0) {
            setCustomFields(selectMultiPipeline.customFields)
        }
    }, [selectMultiPipeline])

    useModalEffect({
        showModal: showDetailProductModal,
        setShowModal: setShowDetailProductModal,
        modalId: `detail-product-pipeline-modal-${dataPipeline?.id}`
    })

    const hasValidAttachments = (attachments: LogAttachment[] | undefined): boolean => {
        if (!attachments || attachments.length === 0) {
            return false;
        }

        const isValidUrl = (url: string): boolean => {
            try {
                new URL(url);
                return true;
            } catch {
                return false;
            }
        };

        return attachments.some(attachment => isValidUrl(attachment.url));
    };

    return (
        <>
            <div className="card" style={{ minHeight: "calc(100vh - 1rem)" }}>
                <h3 className="py-3 px-5 d-flex align-items-center gap-3">
                    <i className="fas fa-times fs-3 text-hover-primary cursor-pointer" onClick={() => setShowDetailPipeline(false)}></i>
                    Detail {dataPipeline.merchant_name}
                </h3>
                <div className="card-header border-0">
                    <div className="card-title pt-3 mb-0 gap-4 gap-lg-10 gap-xl-10 nav nav-tabs border-bottom-0">
                        <div className={`fs-4 fw-bolder pb-3 cursor-pointer ${displayMode === 'pipeline' ? 'border-bottom border-3 border-primary' : 'text-muted'}`} onClick={() => setDisplayMode('pipeline')}>{settings.pipeline_title}</div>
                        <div className={`fs-4 fw-bolder pb-3 cursor-pointer ${displayMode === 'log' ? 'border-bottom border-3 border-primary' : 'text-muted'}`} onClick={() => setDisplayMode('log')}>Log</div>
                    </div>
                </div>

                <div className="card-body row" style={{ flex: "none" }}>
                    <div className={`${displayMode === "pipeline" ? "" : "d-none"}`}>
                        {
                            isLoadingDataDetailPipeline ?
                                <div className="loading-overlay">
                                    <div className="loader"></div>
                                </div>
                                :
                                Object.keys(customLead).map((key, index) => {
                                    return (
                                        <div className="row pb-2 col-12" key={index}>
                                            <div className="col-4 text-end" style={{ fontWeight: "500", color: "#767373" }}>{customLead[key].name}</div>
                                            <div className="col-8" style={{ fontWeight: "500", color: "black", width: 'fit-content' }}>{customLead[key].value}</div>
                                        </div>
                                    )
                                })
                        }
                    </div>
                    <div className={`${displayMode === "log" ? "" : "d-none"}`}>
                        {
                            isLoadingDataLogPipeline ?
                                <div className="loading-overlay">
                                    <div className="loader"></div>
                                </div>
                                :
                                log.map((dataLog: Log, index) => {
                                    const progressName = dataLog.lead_progress_name;
                                    const subProgressName = dataLog.lead_sub_progress_name;
                                    const progressColor = dataLog.lead_progress_color;
                                    const newProgress = checkIsCompanyTanrise(companyId, user) ? intl.formatMessage({ id: "FORM.LABEL.CONTACTED" }) : intl.formatMessage({ id: "FORM.LABEL.NEW" })
                                    const badgeContent = checkIsNewProgress(progressName) ? newProgress : subProgressName ? `${progressName} (${subProgressName})` : progressName;
                                    const badgeStyle = progressColor ? { backgroundColor: progressColor, color: isColorDarkFromHex(progressColor) ? "white" : "black" } : {};

                                    var dataDescription = (dataLog.description).replaceAll("\n", "<br/>").trim()

                                    return (
                                        <div className="d-print-none border border-dashed border-gray-300 card-rounded p-9 bg-lighten mb-3" key={index}>
                                            <div className="mb-1">
                                                <BadgeTooltip tooltipText={badgeContent} badgeClass={progressColor ? '' : 'badge-light-info'} style={badgeStyle}>
                                                    {badgeContent}
                                                </BadgeTooltip>
                                            </div>
                                            <h6 className="mb-8 fw-boldest text-gray-600">{dataLog.creator_name}</h6>
                                            <div className="mb-6">
                                                <div className="fw-bold text-gray-600 fs-7">{intl.formatMessage({ id: "MASTERS.DATATABLE.COLUMNS.DATE_STARTED" })}:</div>
                                                <div className="fw-bolder text-gray-800 fs-6">{moment(dataLog.date_created).format('dddd, DD MMM YYYY HH:mm')}</div>
                                            </div>
                                            <div className="mb-6">
                                                <div className="fw-bold text-gray-600 fs-7">{user.data.company_id === companyId.juraganMaterial ? intl.formatMessage({ id: "MASTERS.DATATABLE.COLUMNS.PREV_PROGRESS_DURATION" }) : intl.formatMessage({ id: "MASTERS.DATATABLE.COLUMNS.DURATION" })}:</div>
                                                <div className="fw-bolder text-gray-800 fs-6">{dataLog.duration}</div>
                                            </div>
                                            <div className={`${hasValidAttachments(dataLog.attachments) ? "mb-6" : ""}`}>
                                                <div className="fw-bold text-gray-600 fs-7">{intl.formatMessage({ id: "MASTERS.DATATABLE.COLUMNS.DESCRIPTION" })}:</div>
                                                <div className="fs-6 d-flex align-items-center">
                                                    {dataDescription ? <ExpandableElement text={dataDescription} maxHeight={45} /> : "-"}
                                                </div>
                                            </div>
                                            {
                                                hasValidAttachments(dataLog.attachments) &&
                                                <div>
                                                    <div className="fw-bold text-gray-600 fs-7">{intl.formatMessage({ id: "MASTERS.DATATABLE.COLUMNS.ATTACHMENT" })}:</div>
                                                    <div className="fw-bolder fs-6 text-gray-800 d-flex align-items-center">
                                                        {
                                                            dataLog.attachments.map((attachment: LogAttachment, index: number) => {
                                                                return (
                                                                    <span key={index}>
                                                                        <a href={attachment.url} target="_blank" rel="noopener noreferrer">{intl.formatMessage({ id: "FORM.ACTION.VIEW_FILE" })} {index + 1}</a>
                                                                        {index < dataLog.attachments.length - 1 && ', '}
                                                                    </span>
                                                                )
                                                            })
                                                        }
                                                    </div>
                                                </div>
                                            }
                                        </div>
                                    )
                                })
                        }
                    </div>
                </div>
            </div>

            {
                showDetailProductModal && <DetailProductPipeline pipeline={dataPipeline! as Pipeline} />
            }
        </>
    )
}

export { ChatDetailPipeline }